import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHubStore, probeHub, ensureHubSocket } from '../lib/hub.js';
import { buscarCajaPrincipal } from '../lib/enlace.js';
import { useDeviceModeStore, usarModo } from '../store/deviceModeStore.js';
import { usarPlan } from '../lib/plan.js';
import { useSessionStore } from '../store/sessionStore.js';
import { useAuthStore } from '../store/authStore.js';
import { embeddedHubAvailable, startEmbeddedHub, mantenerPantalla, LOCAL_HUB_URL } from '../native/hubRuntime.js';
import { useConfirmacion } from '../components/ConfirmSheet.jsx';
import { useActualizacionStore } from '../lib/actualizacion.js';
import { puedeActualizarse, infoApp } from '../native/actualizador.js';
import styles from './HubSetupScreen.module.css';

// "Este aparato": para qué es (caja o comandera) y cómo se conecta con la caja
// principal.
//
// Antes esto era una sola pantalla larga donde había que teclear una IP que
// solo aparecía en letra chica en la caja, y en Android "Ser la caja" nunca
// terminaba de cargar. Ahora la caja principal se anuncia a la nube y los demás
// aparatos la encuentran solos al iniciar sesión (lib/enlace.js); teclear la
// dirección queda como último recurso, escondido.

// Mensajes de error que entiende quien instala, no los del navegador.
const errorDeConexion = (err) => {
  if (err?.name === 'AbortError') return 'No responde. Revisa que este aparato esté en el mismo WiFi que la caja principal.';
  if (/Invalid URL/i.test(err?.message ?? '')) return 'Esa dirección no es válida. Ejemplo: 192.168.1.20';
  if (err instanceof SyntaxError || /no es una caja/i.test(err?.message ?? '')) return 'En esa dirección hay otro aparato, no la caja principal.';
  return 'No hay nada en esa dirección. Usa la que aparece en la caja principal, en Este aparato.';
};

export default function HubSetupScreen() {
  const navigate  = useNavigate();
  const modo      = usarModo();
  const { servicioEnMesa, cocina } = usarPlan();
  const dentro    = useAuthStore((s) => s.isAuthenticated);
  const token     = useAuthStore((s) => s.token);
  const compartida    = useDeviceModeStore((s) => s.compartida);
  const setCompartida = useDeviceModeStore((s) => s.setCompartida);
  const setModo       = useDeviceModeStore((s) => s.setModo);
  const session   = useSessionStore((s) => s.session);
  const { pedir, Confirmacion } = useConfirmacion();

  const hubUrl    = useHubStore((s) => s.hubUrl);
  const hubName   = useHubStore((s) => s.hubName);
  const connected = useHubStore((s) => s.connected);
  const status    = useHubStore((s) => s.status);
  const embedded  = useHubStore((s) => s.embedded);
  const setHub    = useHubStore((s) => s.setHub);
  const clearHub  = useHubStore((s) => s.clearHub);
  const setEmbedded   = useHubStore((s) => s.setEmbedded);
  const setAutoEnlace = useHubStore((s) => s.setAutoEnlace);

  const [avisoModo, setAvisoModo] = useState(null);
  const [cajaName,  setCajaName]  = useState(hubName ?? 'Caja principal');
  const [arrancando, setArrancando] = useState(false);
  const [buscando,  setBuscando]  = useState(false);
  const [mensaje,   setMensaje]   = useState(null);   // { tipo: 'ok' | 'error', texto }
  const [input,     setInput]     = useState(hubUrl && !embedded ? hubUrl.replace(/^http:\/\//, '') : '');
  const [probando,  setProbando]  = useState(false);
  const [live,      setLive]      = useState(status);
  const puedeSerCaja = embeddedHubAvailable() && modo === 'caja';

  // Regresar a donde se vino; si se llegó directo, al inicio que le toca.
  const volver = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(dentro ? '/' : '/login', { replace: true });
  };

  // Pasar a comandera con un turno abierto no lo cierra: el turno es del
  // cajón y su corte se hace desde una caja. Se avisa en vez de impedirlo.
  const cambiarModo = (v) => {
    if (v === modo) return;
    setModo(v);
    setAvisoModo(v === 'comandera' && session
      ? 'Este aparato tenía un turno abierto. No se cerró: haz su corte desde una caja antes de cuadrar el día.'
      : null);
  };

  // Estado de la caja cada 5 s mientras la pantalla está abierta
  useEffect(() => {
    if (!hubUrl) return undefined;
    let vigente = true;
    const tick = () => probeHub(hubUrl).then((r) => { if (vigente) setLive(r.status); }).catch(() => {});
    tick();
    const t = setInterval(tick, 5000);
    return () => { vigente = false; clearInterval(t); };
  }, [hubUrl]);

  const serCajaPrincipal = async () => {
    setMensaje(null);
    setArrancando(true);
    try {
      const st = await startEmbeddedHub({ name: cajaName.trim() || 'Caja principal' });
      setEmbedded(true);
      setHub(LOCAL_HUB_URL, st.name, st.id ?? null);
      setLive(st);
      ensureHubSocket(token);
      // Si este aparato ya tenía turno abierto (por internet), la caja lo adopta
      // ahora y no al primer cobro.
      useSessionStore.getState().revalidar();
      setMensaje({ tipo: 'ok', texto: 'Listo. Las comanderas se conectan solas al iniciar sesión en el mismo WiFi.' });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.message ?? 'No se pudo arrancar la caja principal en esta tablet.' });
    } finally {
      setArrancando(false);
    }
  };

  const dejarDeSerCaja = async () => {
    if (!(await pedir({
      titulo: '¿Dejar de ser la caja principal?',
      texto: 'Las comanderas pasarán a trabajar por internet. Cierra la app después para apagar la caja del todo.',
      confirmar: 'Dejar de ser caja principal', peligro: true,
    }))) return;
    clearHub();
    setAutoEnlace(false);
    mantenerPantalla(false);
    setLive(null);
    ensureHubSocket(token);
  };

  const buscar = async () => {
    setMensaje(null);
    setBuscando(true);
    try {
      const caja = await buscarCajaPrincipal();
      if (!caja) {
        setMensaje({ tipo: 'error', texto: 'No encontré una caja principal en esta red. Revisa que la caja esté encendida con la app abierta y que este aparato esté en el mismo WiFi.' });
        return;
      }
      setHub(caja.url, caja.name, caja.id);
      ensureHubSocket(token);
      useSessionStore.getState().revalidar();
      setMensaje({ tipo: 'ok', texto: `Conectado a la caja principal «${caja.name}».` });
    } catch {
      setMensaje({ tipo: 'error', texto: 'Para buscar la caja principal hace falta internet en este aparato.' });
    } finally {
      setBuscando(false);
    }
  };

  const conectarAMano = async (e) => {
    e?.preventDefault();
    setMensaje(null);
    setProbando(true);
    try {
      const { url, status: st } = await probeHub(input);
      setHub(url, st.name, st.id ?? null);
      setLive(st);
      ensureHubSocket(token);
      useSessionStore.getState().revalidar();
      setMensaje({ tipo: 'ok', texto: `Conectado a la caja principal «${st.name}».` });
    } catch (err) {
      setMensaje({ tipo: 'error', texto: errorDeConexion(err) });
    } finally {
      setProbando(false);
    }
  };

  const desconectar = async () => {
    if (!(await pedir({
      titulo: '¿Desconectar de la caja principal?',
      texto: 'Este aparato trabajará por internet y ya no se conectará solo a la caja. Para volver, usa "Buscar la caja principal".',
      confirmar: 'Desconectar', peligro: true,
    }))) return;
    clearHub();
    setAutoEnlace(false);
    setLive(null);
    ensureHubSocket(token);
  };

  const cloud = live?.cloud;
  const direcciones = (live?.urls ?? []).map((u) => u.replace(/^http:\/\//, ''));

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={volver}>← Volver</button>
          <h1 className={styles.title}>Este aparato</h1>
        </div>

        {/* ── Para qué es ─────────────────────────────────────────────── */}
        {dentro && (
          <>
            <label className={styles.label}>Para qué es</label>
            <div className={styles.modos}>
              {[
                { v: 'caja', t: 'Caja', d: 'Cobra, abre y cierra el turno. La tablet del mostrador.' },
                servicioEnMesa && { v: 'comandera', t: 'Comandera', d: cocina
                  ? 'Toma las órdenes en las mesas y las manda a cocina. La caja cobra.'
                  : 'Toma las órdenes en las mesas. La caja cobra.' },
              ].filter(Boolean).map(({ v, t, d }) => (
                <button
                  key={v}
                  type="button"
                  className={`${styles.modo} ${modo === v ? styles.modoActivo : ''}`}
                  aria-pressed={modo === v}
                  onClick={() => cambiarModo(v)}
                >
                  <strong>{t}</strong>
                  <small>{d}</small>
                </button>
              ))}
            </div>
            {avisoModo && <p className={styles.error} style={{ marginBottom: 12 }}>{avisoModo}</p>}

            {servicioEnMesa && (
              <label className={styles.check}>
                <input type="checkbox" checked={compartida} onChange={(e) => setCompartida(e.target.checked)} />
                <span>
                  <strong>La usan varios meseros</strong>
                  <small>Cada quien se identifica con su PIN al tomar una mesa, y el aparato lo suelta al terminar con ella.</small>
                </span>
              </label>
            )}
          </>
        )}

        {/* ── Caja principal ──────────────────────────────────────────── */}
        <label className={styles.label}>Caja principal</label>
        <p className={styles.lead}>
          Sin caja principal todo funciona por internet: los aparatos del mismo negocio comparten
          mesas y cuentas. Con una caja principal en el WiFi del local, siguen trabajando aunque se
          vaya el internet.
        </p>

        {embedded ? (
          <div className={styles.estadoCaja}>
            <p className={styles.estadoTitulo}><span className={styles.puntoOk} /> Esta tablet es la caja principal</p>
            <p className={styles.hint}>
              Las comanderas se conectan solas al iniciar sesión en el mismo WiFi.
              {live?.devices ? ` Conectados ahora: ${live.devices}.` : ''}
            </p>
            <p className={styles.hint}>Deja la app abierta y la tablet cargando: la pantalla no se apaga mientras sea la caja.</p>
            {direcciones.length > 0 && (
              <p className={styles.hint}>Dirección en la red, por si hace falta teclearla: <strong>{direcciones.join(' · ')}</strong></p>
            )}
            <button className="btn btn--ghost btn--full" onClick={dejarDeSerCaja}>Dejar de ser la caja principal</button>
          </div>
        ) : hubUrl ? (
          <div className={styles.estadoCaja}>
            <p className={styles.estadoTitulo}>
              <span className={connected || !dentro ? styles.puntoOk : styles.puntoMal} />
              Conectado a la caja principal «{live?.name ?? hubName ?? hubUrl}»
            </p>
            {dentro && !connected && (
              <p className={styles.hint}>La caja no contesta en este momento. Si cambió de dirección, este aparato la vuelve a encontrar solo.</p>
            )}
            <button className="btn btn--ghost btn--full" onClick={desconectar}>Desconectar</button>
          </div>
        ) : (
          <div className={styles.acciones}>
            {puedeSerCaja && (
              <>
                <div className={styles.row}>
                  <input className="input" placeholder="Nombre de la caja" value={cajaName} onChange={(e) => setCajaName(e.target.value)} />
                </div>
                <button className="btn btn--primary btn--full" type="button" onClick={serCajaPrincipal} disabled={arrancando}>
                  {arrancando ? <><span className="spinner" /> Preparando la caja…</> : 'Usar esta tablet como caja principal'}
                </button>
              </>
            )}
            {dentro ? (
              <button className={`btn ${puedeSerCaja ? 'btn--ghost' : 'btn--primary'} btn--full`} type="button" onClick={buscar} disabled={buscando}>
                {buscando ? <><span className="spinner" /> Buscando…</> : 'Buscar la caja principal'}
              </button>
            ) : (
              <p className={styles.hint}>Al iniciar sesión, este aparato busca solo la caja principal de tu negocio.</p>
            )}
          </div>
        )}

        {mensaje && <p className={mensaje.tipo === 'ok' ? styles.okMsg : styles.error}>{mensaje.texto}</p>}

        {/* Último recurso: la dirección a mano */}
        {!embedded && (
          <details className={styles.aMano}>
            <summary>Conectar escribiendo la dirección</summary>
            <form className={styles.form} onSubmit={conectarAMano}>
              <p className={styles.hint}>La dirección aparece en la caja principal, en Este aparato. Ejemplo: 192.168.1.20</p>
              <div className={styles.row}>
                <input
                  className="input"
                  placeholder="192.168.1.20"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
                <button className="btn btn--primary" type="submit" disabled={probando || !input.trim()}>
                  {probando ? <span className="spinner" /> : 'Conectar'}
                </button>
              </div>
            </form>
          </details>
        )}

        <SeccionApp />

        {/* Detalle para quien da soporte */}
        {(hubUrl || embedded) && dentro && (
          <div className={styles.status}>
            <div className={styles.statusRow}>
              <span>Caja ↔ internet</span>
              <strong className={cloud?.online ? styles.ok : styles.bad}>
                {cloud ? (cloud.online ? 'Con internet' : 'Sin internet (sigue trabajando)') : '—'}
              </strong>
            </div>
            <div className={styles.statusRow}>
              <span>Ventas y cuentas por subir</span>
              <strong>{cloud?.pending ?? 0}</strong>
            </div>
            <div className={styles.statusRow}>
              <span>Catálogo en la caja</span>
              <strong>{live?.catalog?.products ?? 0} productos · {live?.catalog?.tables ?? 0} mesas</strong>
            </div>
            <div className={styles.statusRow}>
              <span>Turno</span>
              <strong>{live?.session ? `Abierto${live.session.cashier_name ? ` · ${live.session.cashier_name}` : ''}` : 'Sin turno abierto'}</strong>
            </div>
          </div>
        )}
      </div>
      {Confirmacion}
    </div>
  );
}

// Versión de la app y "Buscar actualización". Solo en la app de Android: es
// donde el que instala comprueba qué versión quedó y, si alguien tocó
// "Después", desde donde se actualiza sin esperar al siguiente aviso.
function SeccionApp() {
  const [instalada, setInstalada] = useState(null);
  const [buscando, setBuscando]   = useState(false);
  const [resultado, setResultado] = useState(null);
  const ultima = useActualizacionStore((s) => s.ultima);
  const { revisar, abrir } = useActualizacionStore.getState();
  const aplica = puedeActualizarse();

  useEffect(() => {
    if (aplica) infoApp().then(setInstalada).catch(() => {});
  }, [aplica]);

  if (!aplica) return null;

  const buscar = async () => {
    setBuscando(true); setResultado(null);
    const r = await revisar({ forzar: true });
    setBuscando(false);
    if (r === 'al-dia') setResultado({ tipo: 'ok', texto: 'Tienes la versión más reciente.' });
    else if (r === 'sin-red') setResultado({ tipo: 'error', texto: 'No se pudo revisar: hace falta internet.' });
  };

  return (
    <>
      <label className={styles.label}>La app</label>
      <div className={styles.estadoCaja}>
        <p className={styles.estadoTitulo}>
          Versión {instalada?.versionName && instalada.versionName !== '1.0' ? instalada.versionName : 'anterior a las actualizaciones automáticas'}
        </p>
        {ultima ? (
          <>
            <p className={styles.hint}>Hay una versión nueva: <strong>{ultima.versionName}</strong>.</p>
            <button className="btn btn--primary btn--full" onClick={abrir}>Actualizar</button>
          </>
        ) : (
          <button className="btn btn--ghost btn--full" onClick={buscar} disabled={buscando}>
            {buscando ? <><span className="spinner" /> Buscando…</> : 'Buscar actualización'}
          </button>
        )}
        {resultado && <p className={resultado.tipo === 'ok' ? styles.okMsg : styles.error}>{resultado.texto}</p>}
      </div>
    </>
  );
}

